import { useEffect, useState } from "react";
import { Alert, Button, Field, Input, useToast } from "@app/ui";
import { updateProduct } from "../api/dpp.js";
import { useT } from "../i18n.js";
import type { Product, ProductDetail } from "../types.js";

/**
 * The two customs codes of a product (CN commodity code, Turkish GTİP). Each is
 * a product column, not a schema attr, so each saves on its own button. Owns
 * its drafts, which reset whenever the saved values change.
 */
export function ProductCustomsCodes({
  orgId,
  product,
  canWrite,
  onUpdated,
  onError,
}: {
  orgId: string;
  product: ProductDetail;
  canWrite: boolean;
  onUpdated: (updated: Product) => void;
  onError: (err: unknown) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [commodityCode, setCommodityCode] = useState(product.commodityCode ?? "");
  const [gtip, setGtip] = useState(product.gtip ?? "");
  const [saving, setSaving] = useState<"code" | "gtip" | null>(null);

  useEffect(() => setCommodityCode(product.commodityCode ?? ""), [product.commodityCode]);
  useEffect(() => setGtip(product.gtip ?? ""), [product.gtip]);

  // Empty input clears the code (null), mirroring the gtin PATCH semantics.
  const save = async (which: "code" | "gtip") => {
    setSaving(which);
    try {
      const raw = (which === "code" ? commodityCode : gtip).trim();
      const updated = await updateProduct(
        orgId,
        product.id,
        which === "code" ? { commodityCode: raw === "" ? null : raw } : { gtip: raw === "" ? null : raw },
      );
      onUpdated(updated);
      toast.success(t("product.saved"));
    } catch (e) {
      onError(e);
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      {/* Commodity code lives next to the GTIN identity data, but saves on its
          own — it is a product column (registry/customs), not a schema attr. */}
      <Field
        className="pd-head-code"
        label={t("products.commodityCode")}
        help={t("products.commodityCodeHelp")}
      >
        <div className="pd-head-code-row">
          <Input
            disabled={!canWrite}
            value={commodityCode}
            onChange={(e) => setCommodityCode(e.target.value)}
          />
          {canWrite && (
            <Button
              loading={saving === "code"}
              disabled={commodityCode.trim() === (product.commodityCode ?? "")}
              onClick={() => void save("code")}
            >
              {t("common.save")}
            </Button>
          )}
        </div>
      </Field>
      {/* Turkish export declaration side of the same customs match (draft TR
          ESPR Art. 17): 12-digit GTİP whose first 8 digits are the CN code. */}
      <Field className="pd-head-code" label={t("products.gtip")} help={t("products.gtipHelp")}>
        <div className="pd-head-code-row">
          <Input disabled={!canWrite} value={gtip} onChange={(e) => setGtip(e.target.value)} />
          {canWrite && (
            <Button
              loading={saving === "gtip"}
              disabled={gtip.trim() === (product.gtip ?? "")}
              onClick={() => void save("gtip")}
            >
              {t("common.save")}
            </Button>
          )}
        </div>
      </Field>
      {product.gtip &&
        product.commodityCode &&
        product.gtip.slice(0, 8) !== product.commodityCode.slice(0, 8) && (
          <Alert type="warning">{t("products.gtipMismatch")}</Alert>
        )}
    </>
  );
}
